<template>
  <Panel module="Nixia" layout="large" :showFilter="false" :showSearch="false" :showButtons="false">
    <div slot="content-main" class="nixia-chat-layout">
      <NixiaConversationSidebar />

      <div class="nixia-chat-main">
        <div ref="scrollArea" class="nixia-chat-main-scroll">
          <div v-if="messages.length === 0" class="nixia-chat-main-empty">
            <NixiaWelcome />
          </div>

          <div v-else class="nixia-chat-main-thread">
            <div v-if="currentHasMore" class="nixia-chat-main-load-more">
              <button
                type="button"
                class="nixia-chat-main-load-more-button"
                :disabled="loadingMoreMessages"
                @click="loadMore"
              >
                {{ loadingMoreMessages ? "Carregando..." : "Carregar mensagens anteriores" }}
              </button>
            </div>

            <NixiaMessageBubble v-for="message in messages" :key="message.id" :message="message" />
            <NixiaTypingIndicator :visible="typing" />
          </div>
        </div>

        <div class="nixia-chat-main-footer">
          <div class="nixia-chat-main-footer-inner">
            <div class="nixia-chat-main-status">
              <NixiaConnectionStatus />
            </div>
            <NixiaChatInput @send="onSend" />
            <p class="nixia-chat-main-disclaimer">
              A Nixia é uma IA e pode cometer erros. Por favor, verifique as respostas.
            </p>
          </div>
        </div>
      </div>

      <NixiaVideoPreviewModal />
    </div>
  </Panel>
</template>

<script>
import Panel from "@/ui/layout/Panel";
import { mapState, mapActions, mapGetters, mapMutations } from "vuex";

import NixiaConversationSidebar from "../components/NixiaConversationSidebar.vue";
import NixiaConnectionStatus from "../components/NixiaConnectionStatus.vue";
import NixiaWelcome from "../components/NixiaWelcome.vue";
import NixiaMessageBubble from "../components/NixiaMessageBubble.vue";
import NixiaTypingIndicator from "../components/NixiaTypingIndicator.vue";
import NixiaChatInput from "../components/NixiaChatInput.vue";
import NixiaVideoPreviewModal from "../components/NixiaVideoPreviewModal.vue";

export default {
  name: "NixiaChat",
  components: {
    Panel,
    NixiaConversationSidebar,
    NixiaConnectionStatus,
    NixiaWelcome,
    NixiaMessageBubble,
    NixiaTypingIndicator,
    NixiaChatInput,
    NixiaVideoPreviewModal,
  },
  data() {
    return {
      keepScrollPosition: false,
    };
  },
  computed: {
    ...mapState("nixia", ["typing", "currentConversationId", "loadingMoreMessages"]),
    ...mapGetters("nixia", ["currentMessages", "currentHasMore"]),
    messages() {
      return this.currentMessages;
    },
  },
  watch: {
    messages() {
      if (this.keepScrollPosition) return;
      this.scrollToEnd();
    },
  },
  mounted() {
    this.removeLoading(["panel"]);
  },
  methods: {
    ...mapMutations("generic", ["removeLoading"]),
    ...mapActions("nixia", ["sendMessage", "createConversation", "loadMoreMessages"]),
    async loadMore() {
      const scrollEl = this.$refs.scrollArea;
      const heightBefore = scrollEl ? scrollEl.scrollHeight : 0;

      this.keepScrollPosition = true;

      try {
        await this.loadMoreMessages(this.currentConversationId);
      } finally {
        this.$nextTick(() => {
          if (scrollEl) scrollEl.scrollTop += scrollEl.scrollHeight - heightBefore;
          this.keepScrollPosition = false;
        });
      }
    },
    async onSend(message) {
      let conversationId = this.currentConversationId;

      if (!conversationId) {
        try {
          const conversation = await this.createConversation();
          conversationId = conversation.id;
        } catch {
          return;
        }
      }

      this.sendMessage({ conversationId, message });
    },
    scrollToEnd() {
      this.$nextTick(() => {
        const scrollEl = this.$refs.scrollArea;
        if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.nixia-chat-layout {
  display: flex;
  height: calc(100vh - 120px);
  min-height: 0;
}

.nixia-chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.nixia-chat-main-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.nixia-chat-main-scroll::-webkit-scrollbar {
  width: 8px;
}

.nixia-chat-main-scroll::-webkit-scrollbar-thumb {
  background-color: var(--scrollbar-thumb);
  border-radius: 20px;
}

.nixia-chat-main-scroll::-webkit-scrollbar-track {
  background-color: transparent;
}

.nixia-chat-main-empty {
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.nixia-chat-main-thread {
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 24px 12px;
}

.nixia-chat-main-load-more {
  display: flex;
  justify-content: center;
}

.nixia-chat-main-load-more-button {
  border: 1px solid var(--border-subtle);
  border-radius: 20px;
  background: transparent;
  color: var(--text-muted);
  font-size: 12px;
  padding: 6px 16px;
  cursor: pointer;
}

.nixia-chat-main-load-more-button:disabled {
  cursor: default;
  opacity: 0.6;
}

.nixia-chat-main-footer {
  flex-shrink: 0;
  border-top: 1px solid var(--border-subtle);
  padding: 12px 24px 16px;
}

.nixia-chat-main-footer-inner {
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
}

.nixia-chat-main-status {
  display: flex;
  justify-content: flex-end;
  padding-bottom: 8px;
}

.nixia-chat-main-disclaimer {
  margin: 8px 0 0;
  text-align: center;
  font-size: 11px;
  color: var(--text-muted);
}
</style>
